// shared-types/src/lead/types.ts - Lead entity (web contact form + CRM fields)

/** Ordered pipeline stages for Kanban (fixed funnel v1). */
export const LEAD_DEAL_STAGE_ORDER = [
    'NEW',
    'CONTACTED',
    'QUALIFIED',
    'DEMO_SCHEDULED',
    'TRIAL',
    'PROPOSAL',
    'NEGOTIATION',
    'WON',
    'LOST',
] as const

export type LeadDealStage = (typeof LEAD_DEAL_STAGE_ORDER)[number]

export const LEAD_DEAL_STAGES: readonly LeadDealStage[] = LEAD_DEAL_STAGE_ORDER

export function isLeadDealStage(value: string): value is LeadDealStage {
    return (LEAD_DEAL_STAGE_ORDER as readonly string[]).includes(value)
}

export type LeadActivityType =
    | 'NOTE'
    | 'CALL'
    | 'EMAIL'
    | 'MEETING'
    | 'STAGE_CHANGE'
    | 'ASSIGNMENT_CHANGE'

export type LeadLostReason = 'PRICE' | 'TIMING' | 'COMPETITOR' | 'NO_FIT' | 'NO_RESPONSE' | 'OTHER'

export type LeadPriority = 'LOW' | 'MEDIUM' | 'HIGH'

export type LeadSource = 'WEB' | 'REFERRAL' | 'OUTBOUND' | 'PARTNER' | 'OTHER'

export interface Lead {
    id: string
    name: string
    company: string
    email: string
    phone: string
    message: string
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date
    /** CRM pipeline stage (DB default NEW). */
    dealStage?: LeadDealStage
    /** Retail `seller` (comisionista), not gestión interna. */
    assignedSellerId?: string | null
    leadSource?: LeadSource | string | null
    estimatedValue?: number | null
    estimatedValueCurrency?: string | null
    expectedCloseAt?: Date | null
    nextFollowUpAt?: Date | null
    priority?: LeadPriority | string | null
    convertedAccountId?: string | null
    lostReason?: string | null
}

/** Payload to create a lead (e.g. from landing form). */
export interface CreateLeadDTO {
    name: string
    company: string
    email: string
    phone: string
    message: string
}

export interface UpdateLeadDTO {
    dealStage?: LeadDealStage
    /** Retail `seller` (comisionista), not gestión interna. */
    assignedSellerId?: string | null
    leadSource?: LeadSource | string | null
    estimatedValue?: number | null
    estimatedValueCurrency?: string | null
    expectedCloseAt?: string | null
    nextFollowUpAt?: string | null
    priority?: LeadPriority | string | null
    convertedAccountId?: string | null
    lostReason?: string | null
}

export interface CreateLeadActivityDTO {
    type: LeadActivityType
    body?: string | null
    metadata?: Record<string, unknown> | null
}

export interface LeadActivity {
    id: string
    leadId: string
    type: LeadActivityType
    body: string | null
    metadata: Record<string, unknown> | null
    createdByGestionUserId: string | null
    createdByName?: string | null
    createdAt: Date
    updatedAt: Date
}

export type LeadCrmDashboardSummary = {
    total: number
    byStage: Record<LeadDealStage, number>
    followUpOverdue: number
}
